import { MoneyOffRounded as MoneyOffRoundedIcon } from 'components/icons';
import StatusChip from 'components/@extended/StatusChip';
import { FormattedDate } from 'components/misc/FormattedDate';
import { RecordLink } from 'components/record-chip/RecordLink';
import { useSelector } from 'store';
import { ClassifierData } from 'types/classifiers';
import { InvoiceExpense } from 'types/invoice-details';
import { NamedEntityMinDto } from 'types/other';
import { cn } from 'utils/cn';
import { getClassifierText } from 'utils/getTranslations';
import { useEmployeeNameFormatter } from 'utils/nameOrder';
import { InvoiceDetailsParameterizedTracker } from '../../InvoiceDetailsChangeTrackers';
import { ItemEditButton } from '../../InvoiceItemRow';

type Props = {
    employee: NamedEntityMinDto | null;
    expenseDate: string | null;
    classifiers: Record<string, ClassifierData>;
    invoiceExpense: InvoiceExpense;
    isNonBillable: boolean | undefined;
    handleOnClickEditButton: () => void;
    hideEditButton: boolean;
};

export const EmployeeDisplay = ({
    employee,
    expenseDate,
    classifiers,
    invoiceExpense,
    isNonBillable,
    handleOnClickEditButton,
    hideEditButton,
}: Props) => {
    const { expense } = invoiceExpense;
    const formatName = useEmployeeNameFormatter();
    const { buttonsDisabled: areButtonsDisabled } = useSelector((state) => state.buttons);

    return (
        <div
            className={cn(
                'opacity-decreasing-when-disabled flex flex-row items-center gap-4',
                hideEditButton ? 'cursor-auto' : 'cursor-pointer'
            )}
        >
            <div className="flex flex-col">
                <div className="opacity-decreasing-when-disabled flex items-center gap-2">
                    {invoiceExpense.employee && (
                        <RecordLink
                            to={`/employee/${invoiceExpense.employee.id}`}
                            className="text-fg hover:text-link w-fit font-semibold whitespace-nowrap no-underline hover:underline"
                            label={formatName(invoiceExpense.employee)}
                        />
                    )}
                    <InvoiceDetailsParameterizedTracker
                        currentParam={employee?.id}
                        displayValue={expense?.employee ? formatName(expense.employee) : undefined}
                        storedParam={expense?.employee?.id}
                        textWrap="nowrap"
                    />
                </div>
                <div className="flex flex-row flex-wrap items-center gap-x-2 gap-y-1">
                    <div className="text-[12px] whitespace-nowrap">
                        <FormattedDate value={expenseDate} fallback="" />
                    </div>
                    {isNonBillable ? (
                        <div className="bg-grey-100 flex justify-center rounded-[4px] p-1">
                            <MoneyOffRoundedIcon fontSize="small" className="text-primary h-[16px] w-[16px]" />
                        </div>
                    ) : null}
                    {(expense?.expenseTypeCode || invoiceExpense.expenseTypeCode) && (
                        <StatusChip
                            className="bg-grey-50 whitespace-normal"
                            label={getClassifierText(
                                classifiers['EXPENSE_TYPE'],
                                invoiceExpense.expenseTypeCode || expense?.expenseTypeCode
                            )}
                            type="custom"
                        />
                    )}
                </div>
            </div>
            {!hideEditButton ? (
                <ItemEditButton onClick={handleOnClickEditButton} disabled={areButtonsDisabled} />
            ) : null}
        </div>
    );
};
